import { response, TaskState } from "@iflyrpa/share";
import { AxiosProxyParams } from "../../types";
import { stringifyError } from "../../utils";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import { rid } from "../../utils/shipinhao/utils";
import type { PublishAction } from "./index";
import { VideoChannelUploader } from "./uploader";

/**
 * 视频号发布 - Mock API 方式
 * 通过调用视频号助手平台的内部API实现发布
 *
 * 核心流程:
 * 1. 获取用户信息 (getUserInfo) - 获取 uin 和 finderUsername
 * 2. 获取上传认证密钥 (getUploadAuthKey) - 获取 authKey
 * 3. 申请上传ID (getUploadId) - 获取 UploadID
 * 4. 上传图片 (uploadImage) - 上传图片内容
 * 5. 确认上传完成 (getUploadedImageInfo) - 获取 DownloadURL
 * 6. 发布图文 (publishDynamic) - 发布到视频号
 */

interface MusicInfo {
    id: string; // 音乐ID (docId)
    name: string; // 音乐名称
    image: string; // 音乐封面图 (albumThumbUrl)
    authorName: string; // 艺术家名称 (artist)
    url: string; // 音乐流媒体URL (mediaStreamingUrl)
    raw: {
        playableInfo: {
            type: number; // 音频类型 (docType)
        };
        bgmSource: number; // BGM来源
        query?: string; // 查询字符串 (bgmQuery)
    };
}

interface UploadAuthKeyResponse {
    errCode: number;
    errMsg: string;
    data?: {
        authKey: string;
        uin?: number;
    };
}

interface UserInfoResponse {
    errCode: number;
    errMsg: string;
    data?: {
        finderUser?: {
            uin: number;
            finderUsername: string;
            nickname?: string;
        };
    };
}

interface ImageUploadResult {
    url: string;
    md5sum: string;
    fileSize: number;
    thumbUrl: string;
    fullThumbUrl: string;
    width: number;
    height: number;
    mediaType: number;
    videoPlayLen: number;
    urlCdnTaskId: string;
}

interface PublishResponse {
    errCode: number;
    errMsg: string;
    data?: {
        baseResp?: {
            errcode: number;
            errmsg: string;
        };
    };
}

function generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * 获取13位时间戳
 */
function getTimeStamp(length: number): string {
    return Date.now().toString().substring(0, length);
}

/**
 * 获取用户信息
 * 根据 index_minify.js Line 44701 实现
 */
async function getUserInfo(cookies: string, http: Http): Promise<UserInfoResponse> {
    // 构建请求体，与源码保持一致
    const requestBody = {
        timestamp: getTimeStamp(13), // 13位时间戳
        _log_finder_uin: "",
        _log_finder_id: "",
        rawKeyBuff: null,
        pluginSessionId: null,
        scene: 7,
        reqScene: 7,
    };

    // 使用 POST 请求，不是 GET
    const response = await http.api<UserInfoResponse>({
        method: "post",
        url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/auth/auth_data",
        data: requestBody,
        headers: {
            cookie: cookies,
            referer: "https://channels.weixin.qq.com",
            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
        defaultErrorMsg: "获取用户信息失败",
    });

    // 如果获取到用户信息，调用 getUploadAuthKey 获取 uin
    if (response?.data?.finderUser) {
        const finderUsername = response.data.finderUser.finderUsername || "";
        const authKeyData = await getUploadAuthKey(cookies, finderUsername, http);

        // 将 uin 设置到 finderUser 上
        if (authKeyData?.data?.uin) {
            response.data.finderUser.uin = authKeyData.data.uin;
        } else {
            // 如果没有获取到 uin，设置错误信息
            response.errCode = authKeyData?.errCode || -1;
            response.errMsg = authKeyData?.errMsg || "获取 uin 失败";
        }
    }

    return response;
}

/**
 * 获取上传认证密钥
 * 根据 index_minify.js Line 44740 实现
 */
async function getUploadAuthKey(cookies: string, finderUsername: string, http: Http): Promise<UploadAuthKeyResponse> {
    // 构建请求体，与源码保持一致
    const requestBody = {
        timestamp: getTimeStamp(13), // 13位时间戳
        _log_finder_id: finderUsername, // 使用 finderUsername
        rawKeyBuff: null,
    };

    const response = await http.api<UploadAuthKeyResponse>({
        method: "post",
        url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/helper/helper_upload_params",
        data: requestBody,
        headers: {
            cookie: cookies,
            referer: "https://channels.weixin.qq.com",
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
        defaultErrorMsg: "获取上传认证密钥失败",
    });

    return response;
}

async function uploadImageComplete(
    imagePath: string,
    taskId: string,
    finderUsername: string,
    uin: number,
    authKey: string,
    http: Http,
): Promise<{
    DownloadURL?: string;
    errMsg?: string;
    md5?: string;
    width?: number;
    height?: number;
    fileSize?: number;
}> {
    const uploader = new VideoChannelUploader({
        chunkSize: 8 * 1024 * 1024,
        concurrentLimit: 4,
        http,
    });

    const result = await uploader.upload(imagePath, uin, authKey, {
        onProgress: (p) => console.log(`上传进度: ${p.percent}%`),
    });

    return {
        DownloadURL: result.downloadUrl,
        md5: result.md5,
        fileSize: result.fileSize,
        width: result.width,
        height: result.height,
    };
}

/**
 * 构建位置信息
 */
function buildLocation(address?: {
    name?: string;
    latitude?: number;
    longitude?: number;
    city?: string;
    address?: string;
    uid?: string;
}): Record<string, unknown> {
    if (!address || !address.city) {
        return {
            latitude: 0,
            longitude: 0,
            city: "",
            poiClassifyId: "",
        };
    }

    if (address.uid) {
        // 有完整POI信息
        return {
            latitude: address.latitude || 0,
            longitude: address.longitude || 0,
            city: address.city,
            poiName: address.name || "",
            address: address.address || "",
            poiClassifyId: address.uid,
        };
    }

    // 只有坐标
    return {
        latitude: address.latitude || 0,
        longitude: address.longitude || 0,
        city: address.city,
        poiClassifyId: "",
    };
}

/**
 * 构建话题 XML
 */
function buildTopicXml(content: string, topics?: string[]): string {
    const topicList = topics || [];
    const valueCount = 1 + topicList.length * 2; // 内容(1) + 话题数×2

    let xml = "<finder>";
    xml += "<version>1</version>";
    xml += `<valuecount>${valueCount}</valuecount>`;
    xml += "<style>";
    xml += `<at>${valueCount + 1}</at>`; // at 位置
    xml += "</style>";
    xml += `<value0><![CDATA[${content}]]></value0>`;

    let valueIndex = 1;
    for (const topic of topicList) {
        // 话题节点
        xml += `<value${valueIndex}>`;
        xml += `<topic><![CDATA[#${topic}#]]></topic>`;
        xml += `</value${valueIndex}>`;
        valueIndex++;

        // 空格节点
        xml += `<value${valueIndex}><![CDATA[ ]]></value${valueIndex}>`;
        valueIndex++;
    }

    xml += "</finder>";
    return xml;
}

/**
 * 构建富文本 JSON
 */
function buildRichTextJson(title: string, content: string): string {
    const richText: Array<Record<string, unknown>> = [{ insert: title }, { attributes: { header: 1 }, insert: "\n" }];

    if (content) {
        richText.push({ insert: content });
    }

    return JSON.stringify(richText);
}

/**
 * 构建描述文本
 */
function buildDescription(content: string, topics?: string[]): string {
    let description = content; // 添加话题
    if (topics && topics.length > 0) {
        description += `#${topics.join("#")}`;
    }

    return description;
}

/**
 * 发布图文
 */
async function publishDynamic(
    cookies: string,
    uin: number | undefined,
    fingerPrintDeviceId: string,
    aId: string,
    proxyHttp: Http,
    confParams: {
        token: string;
        enverionment: "development" | "production";
        articleId: string | undefined;
        accountId: number | undefined;
        uid: string | undefined;
        postId: string | undefined;
        eip: string | undefined;
    },
    params: {
        title: string;
        content: string;
        images: ImageUploadResult[];
        finderUsername: string;
        publishType: string;
        topic?: string[];
        collection?: {
            collectionId: string;
            collectionName: string;
        };
        link?: {
            urlType: number;
            link: string;
            title: string;
        };
        address?: {
            name?: string;
            latitude?: number;
            longitude?: number;
            city?: string;
            address?: string;
            uid?: string;
        };
        scheduledPublish?: number;
        music?: MusicInfo; // 添加音乐参数
    },
): Promise<PublishResponse> {
    const topicList = params.topic || [];
    const location = params.address ? buildLocation(params.address) : {};

    // 构建完整的发布参数
    const publishData: Record<string, unknown> = {
        // 系统参数
        clientid: generateUUID(),
        timestamp: getTimeStamp(13), // 13位时间戳
        _log_finder_id: params.finderUsername,
        _log_finder_uin: "",
        videoClipTaskId: "",
        mode: 1,
        sence: 7,
        reqScene: 7,
        scene: 1,
        rawKeyBuff: null,

        // 基础定位信息
        latitude: 0,
        longitude: 0,
        feedLongitude: 0,
        feedLatitude: 0,

        // 发布标记
        originalFlag: 0,
        objectType: 7, // 7=图文
        postFlag: 0,
        isFullPost: 1,
        handleFlag: 2,

        // 话题列表
        topics: topicList,

        // 核心内容对象
        objectDesc: {
            description: buildDescription(params.content, topicList),
            event: {},
            extReading: {
                ...params.link,
                link: params.link?.link?.replace(/[\s\u200b]/g, ""),
            },
            mediaType: 2, // 2=图片

            // 位置信息
            location: location,

            // 话题信息
            topic: {},

            // @提及的用户
            member: {},
            mentionedUser: [],

            mpTitle: "",

            // 媒体列表-图片
            media: params.images,
            // 音乐
            shortTitle: "",

            // 富文本描述
            finderNewlifeDesc: {
                richTextTitle: params.title,
                richTextJson: buildRichTextJson(params.title, params.content),
                fromRichPublisher: 1,
            },
        },
        // traceInfo: { // TODO
        //     traceKey: "FPT_1772941976_740477911",
        //     uploadCdnEnd: 1772941976,
        //     uploadCdnStart: 1772941976
        // }
    };

    // 定时发布（转为10位时间戳）
    if (params.scheduledPublish) {
        publishData.effectiveTime = params.scheduledPublish;
    }

    const objectDesc = publishData.objectDesc as Record<string, unknown>;
    if (topicList.length) {
        objectDesc.topic = {
            finderTopicInfo: buildTopicXml(params.content, topicList),
        };
    }
    if (params.collection) {
        objectDesc.topic = {
            ...(typeof objectDesc.topic === "object" && objectDesc.topic !== null ? objectDesc.topic : {}),
            ...params.collection,
        };
    }

    // 添加音乐信息（根据源码 Line 51809-51823）
    if (params.music?.id) {
        // hotfix 映射值,处理视频号图文音乐无效的问题
        // bgmSource 2：默认，4：搜索
        // docType 0/1 0 对应bgmSource 2， 1对应bgmSource 4
        objectDesc.followPostInfo = {
            musicInfo: {
                docId: params.music.id,
                albumThumbUrl: params.music.image,
                name: params.music.name,
                artist: params.music.authorName,
                mediaStreamingUrl: params.music.url,
                docType: params.music.raw.bgmSource === 2 ? 0 : 1,
            },
            groupId: params.music.id,
            hasBgm: 1,
            bgmSource: params.music.raw.bgmSource,
            bgmQuery: params.music.raw.query || "",
        };
    }

    // 选择正确的URL
    const publishUrl =
        params.publishType === "1"
            ? "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/post/post_create"
            : "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/post/post_draft";

    // 草稿模式需要包装参数
    let requestBody: Record<string, unknown>;
    if (params.publishType === "2") {
        requestBody = {
            _log_finder_id: params.finderUsername,
            objectId: null,
            pluginSessionId: null,
            postReq: publishData,
            rawKeyBuff: "",
            scene: 1,
            timestamp: getTimeStamp(13),
        };
    } else {
        requestBody = publishData;
    }

    const queryData = {
        _aid: aId,
        _rid: rid(),
        _pageUrl: "https://channels.weixin.qq.com/micro/content/post/finderNewLifeCreate",
    };
    const queryString = new URLSearchParams(queryData).toString();

    console.log("1111111111111111", JSON.stringify(requestBody));

    const apiResponse = await proxyHttp.api<PublishResponse>({
        method: "post",
        url: `${publishUrl}?${queryString}`,
        data: requestBody,
        headers: {
            cookie: cookies,
            referer: "https://channels.weixin.qq.com/micro/content/post/finderNewLifeCreate",
            "Content-type": "application/json",
            "finger-print-device-id": fingerPrintDeviceId,
            "x-wechat-uin": uin,
        },
        defaultErrorMsg: "发布图文失败",
    });

    reportLogger({
        token: confParams.token || "",
        enverionment: confParams.enverionment || "development",
        postId: confParams.articleId,
        eip: proxyHttp.proxyInfo,
        accountId: confParams.accountId,
        uid: confParams.uid,
        publishParams: publishData,
        platform: "shipinhao",
    });

    return apiResponse;
}

export const mockAction: PublishAction = async (task, params) => {
    task.logger.info("[shipinhaoPublish] 开始执行视频号发布 - Mock API 方式");
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");
    if (!params.extraParam.fingerPrintDeviceId || !params.extraParam.aId) {
        return response(400, "fingerPrintDeviceId和aId 不能为空", "");
    }

    let currentStep = "初始化"; // 追踪当前执行步骤

    try {
        // 1. 转换 cookies 为字符串
        currentStep = "解析认证信息";
        const cookieString = params.cookies.map((c) => `${c.name}=${c.value}`).join("; ");
        const fingerPrintDeviceId = params.extraParam.fingerPrintDeviceId;
        const aId = params.extraParam.aId;

        // 创建 Http 实例
        const headers = {
            cookie: cookieString,
        };

        const args: AxiosProxyParams = [{ headers }, task.logger, params.proxyLoc, params.accountId, "shipinhao"];

        const http = new Http({ headers });
        const proxyHttp = new Http(...args);

        // 2. 验证参数
        currentStep = "验证发布参数";
        if (!params.banners || params.banners.length === 0) {
            return response(400, "至少需要一张图片", "");
        }

        // 限制图片数量，并确保只包含字符串路径
        const images = params.banners.slice(0, 18).filter((img: unknown) => typeof img === "string") as string[];

        // 3. 获取用户信息
        currentStep = "获取用户信息";
        task.logger.info("[shipinhaoPublish] 获取用户信息...");
        console.log("cookieString", cookieString);
        const userInfo = await getUserInfo(cookieString, http);

        console.log(JSON.stringify(userInfo));
        if (userInfo.errCode !== 0 || !userInfo.data?.finderUser) {
            const errorMsg =
                userInfo.errCode === 300334 || userInfo.errCode === 300333
                    ? "登录失效"
                    : userInfo.errMsg || "获取用户信息失败";
            return response(userInfo.errCode || 500, errorMsg, "");
        }

        const finderUsername = userInfo.data.finderUser.finderUsername;
        console.log(finderUsername);
        // 4. 获取上传认证密钥
        currentStep = "获取上传认证密钥";
        task.logger.info("[shipinhaoPublish] 获取上传认证密钥...");
        const authKeyResponse = await getUploadAuthKey(cookieString, finderUsername, http);

        if (authKeyResponse.errCode !== 0 || !authKeyResponse.data?.authKey) {
            return response(authKeyResponse.errCode || 500, `获取上传认证参数失败: ${authKeyResponse.errMsg}`, "");
        }

        const authKey = authKeyResponse.data.authKey;
        const uin = authKeyResponse.data.uin;

        if (!uin) {
            return response(500, "获取用户 uin 失败", "");
        }

        const taskId = String(generateUUID());

        // 5. 上传图片
        currentStep = "上传图片";
        task.logger.info(`[shipinhaoPublish] 开始上传 ${images.length} 张图片...`);
        const uploadedImages: ImageUploadResult[] = [];

        for (let i = 0; i < images.length; i++) {
            currentStep = `上传图片 ${i + 1}/${images.length}`;
            task.logger.info(`[shipinhaoPublish] ${currentStep}...`);

            const uploadResult = await uploadImageComplete(images[i], taskId, finderUsername, uin, authKey, http);
            console.log(JSON.stringify(uploadResult));
            if (!uploadResult.DownloadURL) {
                return response(500, `图片上传失败: ${uploadResult.errMsg}`, "");
            }

            uploadedImages.push({
                url: `https://finder.video.qq.com${uploadResult.DownloadURL.split("qq.com")[1] || ""}`,
                md5sum: uploadResult.md5 || "",
                fileSize: uploadResult.fileSize || 0,
                thumbUrl: uploadResult.DownloadURL,
                fullThumbUrl: uploadResult.DownloadURL,
                width: uploadResult.width || 0,
                height: uploadResult.height || 0,
                mediaType: 2,
                videoPlayLen: 0,
                urlCdnTaskId: "",
            });
        }

        // 6. 发布图文
        currentStep = "发布图文内容";
        task.logger.info("[shipinhaoPublish] 正在发布...");

        // 转换话题格式：从对象数组提取 name 字段
        const topicNames = params.topic || undefined;

        // 转换位置格式
        const addressInfo = params.address
            ? {
                  name: params.address.name,
                  latitude: params.address.latitude,
                  longitude: params.address.longitude,
                  city: params.address.city_name,
                  address: params.address.address,
                  uid: params.address.poi_id,
              }
            : undefined;

        let publishResult: PublishResponse;
        try {
            publishResult = await publishDynamic(
                cookieString,
                uin,
                fingerPrintDeviceId,
                aId,
                proxyHttp,
                {
                    token: params.huiwenToken || "",
                    enverionment: task.enverionment || "development",
                    articleId: params.articleId,
                    postId: params.articleId,
                    eip: proxyHttp.proxyInfo,
                    accountId: params.accountId,
                    uid: params.uid,
                },
                {
                    title: params.title,
                    content: params.content,
                    images: uploadedImages,
                    finderUsername,
                    publishType: params.publishType || "1",
                    topic: topicNames,
                    link: params.link,
                    address: addressInfo,
                    collection: params.collection,
                    scheduledPublish: params.scheduledPublish,
                    music: params.music, // 传递音乐参数
                },
            );
        } catch (error) {
            const handledError = Http.handleApiError(error);
            // 代理连接超时(599) 与网络/服务端错误(500/502/503/504)：重试耗尽后直接返回代理错误信息，
            // 不再 throw 走 executeAction 的 RPA 兜底。code 归一为 414，使 executeAction 视为终止结果。
            const isProxyOrNetworkError = [599, 500, 502, 503, 504].includes(handledError.code);
            if (isProxyOrNetworkError) {
                // 区分代理错误和普通网络错误
                const isProxyRequest = handledError.extra?.isProxyRequest === true;
                const errorType = handledError.code === 599 || isProxyRequest ? "代理错误" : "网络错误";
                const message = `图文发布失败，${errorType}：${handledError.message}${task.debug ? ` ${http.proxyInfo}` : ""}`;
                task.logger.error(
                    `[shipinhaoPublish] ${errorType}，直接返回: ${message}`,
                    stringifyError(handledError),
                );
                await updateTaskState?.({
                    state: TaskState.FAILED,
                    error: message,
                });
                return { code: 414, data: "", message };
            }
            // 其它错误码维持原有逻辑，抛出以便上层 executeAction 走 RPA 兜底
            throw error;
        }

        task.logger.info(`[shipinhaoPublish] publishResult: ${JSON.stringify(publishResult)} `);

        // 7. 处理发布结果
        const resultCode = publishResult.data?.baseResp?.errcode ?? publishResult.errCode;
        const resultMsg = publishResult.data?.baseResp?.errmsg ?? publishResult.errMsg;

        if (resultCode === 0) {
            task.logger.info("[shipinhaoPublish] 发布成功");
            const publishId = uploadedImages[0]?.thumbUrl?.split("encfilekey=")[1]?.split("&")[0] || "";
            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    response: resultMsg,
                },
            });
            return response(0, "发布成功", publishId);
        }

        // 处理特殊错误码
        let errorMessage = resultMsg;
        if (resultCode === -11224) {
            errorMessage = "视频号管理员完成实名且绑定手机号后才可以发表";
        } else if (resultCode === 300333 || resultCode === 300334) {
            errorMessage = "登录失效";
        } else if (resultCode === 300330) {
            errorMessage = "未登录";
        } else if (resultCode === 300002) {
            errorMessage = "官方平台在校验音乐/位置/定时信息时失败了，请重新编辑后发布";
        }

        task.logger.error(`[shipinhaoPublish] 发布失败: ${errorMessage}`);
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: errorMessage,
        });
        return response(resultCode || 500, errorMessage, "");
    } catch (error) {
        // 使用 Http.handleApiError 规范化错误，保留错误码和结构化信息
        const handledError = Http.handleApiError(error);

        // 构造详细的错误消息
        const errorMsg = handledError.message || "发布失败，请稍后重试";
        const errorCode = handledError.code || 500;

        // 记录完整的错误信息（包括堆栈和额外上下文）
        task.logger.error(`[shipinhaoPublish] 发布流程异常: ${errorMsg}`, stringifyError(error), handledError.extra);

        // 更新任务状态
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: errorMsg,
        });

        return response(errorCode, errorMsg, "");
    }
};
